import React from 'react';
import { useTranslation } from 'react-i18next';
import compose from 'lodash/fp/compose';
import { withTranslation, withAccount } from 'components/hoc';
import styled from 'styled-components';
import Logo from './Admincatlogo.png';
import LogoInverted from './AdminCatinverted.png';
import { Link, useLocation } from 'react-router-dom';
import DashboardTemplate from '../common/DashboardTemplate';
import routes, { redirects } from './routes';
import { useDarkMode } from 'context/DarkModeContext';

const FooterWrapper = styled.footer`
  width: 100%;
  padding: 48px 20px 28px;
  background: ${({ $dark }) =>
    $dark
      ? 'linear-gradient(180deg, #0e1526 0%, #1c2741 100%)'
      : 'linear-gradient(180deg, #eef2ff 0%, #ffffff 88%)'};
  color: ${({ $dark }) => ($dark ? '#dce4ff' : '#1d2946')};
  transition: background 0.3s ease, color 0.3s ease;
`;

const FooterContainer = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const FooterTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
  text-align: center;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    text-align: left;
    gap: 48px;
  }
`;

const BrandColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 320px;

  @media (min-width: 768px) {
    align-items: flex-start;
  }
`;

const LogoImage = styled.img`
  width: 160px;
  height: auto;
`;

const BrandTagline = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${({ $dark }) => ($dark ? '#b7c5eb' : '#4a5d88')};
`;

const FooterLinks = styled.nav`
  width: 100%;
`;

const NavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px 18px;

  @media (min-width: 768px) {
    gap: 16px 32px;
  }
`;

const NavItem = styled.li`
  display: flex;
  justify-content: center;

  @media (min-width: 768px) {
    justify-content: flex-start;
  }
`;

const StyledFooterLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 4px 0;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-decoration: none;
  color: ${({ $dark }) => ($dark ? '#d9e4ff' : '#28457a')};
  background: transparent;
  box-shadow: none;
  position: relative;
  transition: color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -4px;
    width: 100%;
    height: 2px;
    background: ${({ $dark }) => ($dark ? 'rgba(120, 160, 230, 0.55)' : 'rgba(50, 90, 160, 0.45)')};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.2s ease;
  }

  &:hover {
    color: ${({ $dark }) => ($dark ? '#f0f4ff' : '#1c2f55')};
  }

  &:hover::after {
    transform: scaleX(1);
  }
`;

const FooterDivider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid ${({ $dark }) => ($dark ? 'rgba(133, 153, 195, 0.35)' : 'rgba(148, 170, 213, 0.35)')};
  margin: 0;
`;

const CopyRightText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.9rem;
  color: ${({ $dark }) => ($dark ? '#c5d3f2' : '#5c6f92')};
  text-align: center;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    gap: 16px;
  }
`;

const Footer = (props) => {
  const { tr, userPreLang } = props;
  const location = useLocation();
  const {
    account: {
      user: { isAuth },
    },
  } = props;

  const { t } = useTranslation();
  const { isDarkMode } = useDarkMode();
  const effectiveDark = isDarkMode && isAuth;

  const prefersGerman = userPreLang === 'deu';
  const translateLinks = isAuth ? tr : t;
  const translateCopy = prefersGerman ? tr : t;
  const logoSrc = effectiveDark ? LogoInverted : Logo;
  const currentYear = new Date().getFullYear();

  const navigationLinks = [
    { path: '/disclaimer', guestKey: 'Disclaimer', authKey: 'disclaimer' },
    { path: '/user-terms', guestKey: 'User Terms', authKey: 'user terms' },
    { path: '/privacy-statement', guestKey: 'Privacy Policy', authKey: 'privacy policy' },
    { path: '/faq', guestKey: 'FAQs', authKey: 'faqs' },
    { path: '/blog', guestKey: 'Blog', authKey: 'blog' },
  ];

  if (location.pathname === '/admin') {
    return null;
  }

  const footerContent = (
    <FooterWrapper $dark={effectiveDark}>
      <FooterContainer>
        <FooterTop>
          <BrandColumn>
            <LogoImage src={logoSrc} alt="AdminCat" />
          </BrandColumn>
          <FooterLinks>
            <NavList>
              {navigationLinks.map(({ path, guestKey, authKey }) => (
                <NavItem key={path}>
                  <StyledFooterLink $dark={effectiveDark} to={path}>
                    {translateLinks(isAuth ? authKey : guestKey)}
                  </StyledFooterLink>
                </NavItem>
              ))}
            </NavList>
          </FooterLinks>
        </FooterTop>
        <FooterDivider $dark={effectiveDark} />
        <CopyRightText $dark={effectiveDark}>
          <span>© {currentYear} AdminCat</span>
          <span>{translateCopy('All rights reserved')}</span>
        </CopyRightText>
      </FooterContainer>
    </FooterWrapper>
  );

  if (isAuth) {
    return (
      <DashboardTemplate routes={routes} redirects={redirects}>
        {footerContent}
      </DashboardTemplate>
    );
  }

  return footerContent;
};

export default compose(withTranslation, withAccount)(Footer);
